/**
 * lodTexScale — tells the shader which LOD a terrain mesh is, on a vertex attribute.
 *
 * Coarse terrain repeats its texture so many times per pixel that the mip chain averages it to a flat
 * colour, so the far rings get a larger world-space texture (see TERRAIN_LOD_TEX_*). A batch draws
 * every level in one call, so the level cannot be a uniform, and the distance-based alternative is
 * not one either: a UV scaled by a value that varies per fragment is a warp rather than a scale, and
 * smears radially.
 *
 * So the level rides a one-byte attribute holding the same value at every vertex, and the POLICY
 * stays in uniforms — which is what lets the debug sliders retune it without restamping a single
 * geometry. Every level is stamped, lod 0 included: the batches mix levels in one buffer, so the
 * attribute has to exist on every geometry. A mesh that never comes through here (build previews,
 * held items) reads 0, which is lod 0.
 */

import * as THREE from 'three';

const ATTR = 'lodLevel';

/**
 * Stamp `lod` onto a geometry. Call on every geometry a terrain mesh is given — including a rebaked
 * one handed to a mesh that already existed, which is not the same thing as tagging the mesh.
 */
export function setLodLevelAttr(geometry: THREE.BufferGeometry, lod: number): void {
  const count = geometry.getAttribute('position')?.count ?? 0;
  const existing = geometry.getAttribute(ATTR) as THREE.BufferAttribute | undefined;

  if (!existing || existing.count !== count) {
    if (count === 0) {
      if (existing) geometry.deleteAttribute(ATTR);
      return;
    }
    // One byte a vertex (vertexFormat.ts) — an integer the shader reads as a float.
    geometry.setAttribute(ATTR, new THREE.BufferAttribute(new Uint8Array(count).fill(Math.max(0, lod)), 1));
    return;
  }
  if (existing.array[0] === lod) return;
  (existing.array as Uint8Array).fill(lod);
  existing.needsUpdate = true;
}
